import { useMutation } from '@apollo/client';
import React, { FC, useCallback, useMemo, useState } from 'react';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { ErrorMessage, Field, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import TextArea from '@atlaskit/textarea';
import { ApprovalStatus, ApprovalType, Task, UpdateStatus } from '@css-xp/apd-common';
import { useApplicationContext } from '@css-xp/base/common-context';
import { EntitlementPageLink, SlackChannelLink } from '../common';
import { ModalTitle } from '../constants/modal-titles';
import { updateTask as UPDATE_TASK } from '../queries/tasks';
import ErrorNotificationMessage from './error-notification-message';
import './modal.scss';
import { useModalNotification } from './use-modal-notification';

interface RequestModalProps {
  task: Task;
  onComplete?: () => void;
  closeModal: () => void;
}

const RequestReasonModal: FC<RequestModalProps> = ({ task, onComplete, closeModal }) => {
  const [loading, setLoading] = useState<boolean>(false);

  const [updateTask] = useMutation<{ updateTask: { status: UpdateStatus } }>(UPDATE_TASK);

  const userProfile = useApplicationContext();

  const [renderSuccessNotification, renderErrorNotification] = useModalNotification(
    closeModal,
    'request-reason-modal-notification'
  );

  const entitlementPageLink = useMemo(() => {
    if (task.businessKey && task.entityType) {
      return (
        <EntitlementPageLink entityNumber={task.businessKey} entityType={task.entityType} entityId={task.entityId} />
      );
    }
    return '';
  }, [task]);

  const notificationMsg = useMemo(() => {
    if (task.requestType) {
      switch (task.requestType) {
        case ApprovalType.TAX_REFUND:
        case ApprovalType.SUBSCRIPTION_REFUND:
          return (
            <p>
              You’ve asked for supporting reasons for the request {task.taskNumber} for the invoice{' '}
              {entitlementPageLink}
            </p>
          );
        case ApprovalType.PRODUCT_DELETION:
        case ApprovalType.TRIAL_EXTENSION:
        case ApprovalType.ORDER_DISCOUNT:
        case ApprovalType.PRICING_PLAN_CHANGE:
          return (
            <p>
              You’ve asked for supporting reasons for the request {task.taskNumber} for the subscription{' '}
              {entitlementPageLink}
            </p>
          );
        case ApprovalType.QUOTE_EXTENSION:
        case ApprovalType.QUOTE_DISCOUNT:
          return (
            <p>
              You’ve asked for supporting reasons for the request {task.taskNumber} for the quote {entitlementPageLink}
            </p>
          );
      }
    }
    return '';
  }, [task]);

  const handleFormSubmit = useCallback(
    async ({ comment }: { comment: string }) => {
      setLoading(true);
      const userID = userProfile?.userId?.split('@')?.[0];
      try {
        const result = await updateTask({
          variables: {
            taskId: task.taskId,
            approvalStatus: ApprovalStatus.REASONS_REQUESTED,
            id: userID,
            comment,
          },
        });
        const errorMsg = result?.errors?.[0]?.extensions?.message;
        if (errorMsg) {
          throw new Error(errorMsg);
        }
        const status = result?.data?.updateTask?.status ?? '';
        if (status === 'OK') {
          onComplete?.();
          renderSuccessNotification('Request submitted', notificationMsg);
        }
      } catch (e: any) {
        renderErrorNotification(
          'We’re unable to send the request',
          <ErrorNotificationMessage errorMessage={e.message} />
        );
      } finally {
        setLoading(false);
      }
    },
    [task.taskId]
  );

  return (
    <Form onSubmit={handleFormSubmit}>
      {({ formProps }) => (
        <div className="modal-form-wrapper" data-testid="request-reason-modal">
          <form {...formProps}>
            <FormHeader
              title={
                <div className="modal-title-wrapper">
                  <span className="modal-title">{ModalTitle.REQUEST_REASONS}</span>
                </div>
              }
            />
            <p>
              Your response will be added to the request in the Slack channel{' '}
              <SlackChannelLink url={task.slackMessageUrl}>{task.slackChannel}</SlackChannelLink> and Approval Desk.
            </p>
            <FormSection>
              <Field<string, HTMLTextAreaElement>
                name="comment"
                label="Internal comment"
                isRequired={true}
                validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
              >
                {({ fieldProps, error }) => (
                  <>
                    <TextArea
                      {...fieldProps}
                      placeholder="Add comment"
                      testId="reason-comment-input"
                      isRequired={true}
                      isInvalid={!!error}
                    />
                    {error && <ErrorMessage testId="reason-comment-input-error">This field is required</ErrorMessage>}
                  </>
                )}
              </Field>
            </FormSection>
            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="subtle" onClick={closeModal} testId="cancel-btn">
                  Cancel
                </Button>
                <LoadingButton appearance="primary" autoFocus type="submit" isLoading={loading} testId="submit-btn">
                  Submit
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        </div>
      )}
    </Form>
  );
};

export default RequestReasonModal;
